import { Search, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';

interface FilterOption {
  value: string;
  label: string;
  selectedColorClass?: string;
  unselectedColorClass?: string;
}

interface ChipFilter {
  id: string;
  label: string;
  options: FilterOption[];
  value: string[];
  onChange: (value: string[]) => void;
}

interface FilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  chipFilters?: ChipFilter[];
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  alwaysExpanded?: boolean;
  children?: ReactNode;
}

export function FilterBar({
  search,
  onSearchChange,
  searchPlaceholder = 'חיפוש...',
  chipFilters = [],
  onClearFilters,
  hasActiveFilters,
  alwaysExpanded = false,
  children,
}: FilterBarProps) {
  const [showFilters, setShowFilters] = useState(false);
  const hasChipFilters = chipFilters.length > 0 && chipFilters.some((f) => f.options.length > 0);
  const filtersVisible = alwaysExpanded || showFilters;
  const activeCount = chipFilters.reduce((acc, f) => acc + f.value.length, 0);

  return (
    <div className="space-y-3 p-4">
      {/* Search Row */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-gray-400"
            size={18}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="filter-search w-full py-2.5 pr-10 pl-4"
          />
          {search && (
            <button
              onClick={() => onSearchChange('')}
              aria-label="נקה חיפוש"
              className="absolute top-1/2 left-1 inline-flex min-h-10 min-w-10 -translate-y-1/2 items-center justify-center text-gray-400 transition-[color,transform] hover:text-gray-600 active:scale-[0.96]"
            >
              <X size={15} />
            </button>
          )}
        </div>

        {hasChipFilters && !alwaysExpanded && (
          <button
            onClick={() => setShowFilters(!showFilters)}
            aria-label="סינון"
            className={`relative flex min-h-[44px] items-center gap-1.5 rounded-xl border px-3.5 py-2.5 text-sm font-medium transition-[background-color,border-color,color,box-shadow,transform] active:scale-[0.96] ${
              showFilters || hasActiveFilters
                ? 'border-[#0C1A55] bg-[#0C1A55] text-white shadow-sm'
                : 'border-gray-200 bg-white/80 text-gray-600 hover:border-[#3C5FA8]/50 hover:text-[#0C1A55]'
            }`}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2 4h12M4 8h8M6 12h4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <span className="hidden sm:inline">סינון</span>
            {activeCount > 0 && (
              <span
                className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold tabular-nums ${
                  showFilters || hasActiveFilters
                    ? 'bg-white text-[#0C1A55]'
                    : 'bg-[#3C5FA8] text-white'
                }`}
              >
                {activeCount}
              </span>
            )}
          </button>
        )}

        {hasActiveFilters && (
          <button
            onClick={() => {
              onClearFilters();
              setShowFilters(false);
            }}
            aria-label="נקה סינון"
            className="flex min-h-[44px] items-center gap-1 rounded-xl px-3 py-2.5 text-sm font-medium text-red-500 transition-[background-color,color,transform] hover:bg-red-50 hover:text-red-700 active:scale-[0.96]"
          >
            <X size={15} />
            <span className="hidden sm:inline">נקה</span>
          </button>
        )}

        {children}
      </div>

      {/* Filter Chips */}
      {filtersVisible && hasChipFilters && (
        <div className="animate-fadeInFast">
          {alwaysExpanded ? (
            <div className="flex flex-wrap items-start gap-4 pt-1">
              {chipFilters.map((filter) => (
                <div key={filter.id} className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold tracking-wide whitespace-nowrap text-gray-500 uppercase">
                    {filter.label}:
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {filter.options.map((option) => {
                      const isSelected = filter.value.includes(option.value);
                      return (
                        <button
                          key={option.value}
                          onClick={() => {
                            const newValue = isSelected
                              ? filter.value.filter((v) => v !== option.value)
                              : [...filter.value, option.value];
                            filter.onChange(newValue);
                          }}
                          className={`filter-chip ${
                            isSelected
                              ? option.selectedColorClass || 'filter-chip-active'
                              : option.unselectedColorClass || ''
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="glass-surface space-y-4 p-4">
              {chipFilters.map((filter) => (
                <div key={filter.id}>
                  <label className="mb-2 block text-xs font-semibold tracking-wide text-gray-500 uppercase">
                    {filter.label}
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {filter.options.map((option) => {
                      const isSelected = filter.value.includes(option.value);
                      return (
                        <button
                          key={option.value}
                          onClick={() => {
                            const newValue = isSelected
                              ? filter.value.filter((v) => v !== option.value)
                              : [...filter.value, option.value];
                            filter.onChange(newValue);
                          }}
                          className={`filter-chip ${
                            isSelected
                              ? option.selectedColorClass || 'filter-chip-active'
                              : option.unselectedColorClass || ''
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
